<template>
  <div class="page-container financial-overview-page" v-if="hasAccess">
    <portal to="page-name">Financeiro</portal>

    <header class="fo-header">
      <div>
        <span class="fo-eyebrow">Financeiro</span>
        <h1 class="fo-title">Visão geral</h1>
        <p class="fo-subtitle">Como está o dinheiro da clínica hoje e no período escolhido.</p>
      </div>
      <div class="fo-actions">
        <div class="fo-segment" role="group" aria-label="Visão dos resultados">
          <button type="button" :class="{ 'is-active': regime === 'competence' }"
                  @click="setRegime('competence')">Competência</button>
          <button type="button" :class="{ 'is-active': regime === 'cash' }"
                  @click="setRegime('cash')">Caixa</button>
        </div>
        <button type="button" class="btn btn-gray-outline btn-action tooltip tooltip-bottom"
                :data-tooltip="hidden ? 'Mostrar valores' : 'Ocultar valores'"
                :aria-label="hidden ? 'Mostrar valores' : 'Ocultar valores'"
                @click="toggleHidden">
          <fa-icon :icon="['fal', hidden ? 'eye-slash' : 'eye']"/>
        </button>
        <router-link class="btn btn-icon btn-icon-left" to="/financial/statements">
          <fa-icon :icon="['fal', 'file-import']"/>Importar extrato
        </router-link>
        <router-link class="btn btn-primary btn-icon btn-icon-left" v-if="canCreate"
                     to="/financial/transactions/create">
          <fa-icon :icon="['fal', 'plus']"/>Novo lançamento
        </router-link>
      </div>
    </header>

    <attention-bar :attention="overview.attention" :hidden="hidden"/>

    <div class="fo-section">
      <div class="fo-section-title">
        <h2>Hoje</h2>
        <span>{{ todayLabel }}</span>
      </div>
    </div>
    <div class="fo-row fo-row-today">
      <today-card :date="overview.date" :today="overview.today" :due-today="overview.dueToday"
                  :hidden="hidden" :loading="loading.overview"/>
      <accounts-card :accounts="overview.accounts" :card-receivables="overview.cardReceivables"
                     :hidden="hidden" :loading="loading.overview"/>
    </div>

    <div class="fo-section">
      <div class="fo-section-title">
        <h2>Período</h2>
        <span>vale para as seções abaixo</span>
      </div>
      <st-date-period class="fo-period" :start-date.sync="period.startDate"
                      :end-date.sync="period.endDate" :periods="periods" :max-months="6"/>
    </div>
    <div class="fo-row">
      <receivables-card :receivables="periodData.receivables" :period="appliedPeriod"
                        :date="overview.date" :hidden="hidden" :loading="loading.period"/>
      <expenses-card :expenses="periodData.expenses" :regime="regime" :hidden="hidden"
                     :loading="loading.period"/>
    </div>

    <div class="fo-section">
      <div class="fo-section-title">
        <h2>Saúde financeira</h2>
        <span>{{ periodLabel }}</span>
      </div>
    </div>
    <health-band :result="periodData.result" :delinquency="periodData.delinquency"
                 :coverage="overview.coverage" :ticket="periodData.ticket" :period="appliedPeriod"
                 :regime="regime" :hidden="hidden" :loading="loading.period || loading.overview"/>

    <div class="fo-row fo-row-charts">
      <upcoming-chart :weeks="overview.upcoming" :hidden="hidden" :loading="loading.overview"/>
      <evolution-chart :months="months" :regime="regime" :hidden="hidden"
                       :loading="loading.evolution"/>
    </div>
  </div>
  <forbidden v-else/>
</template>

<script>
import localforage from 'localforage';
import moment from 'moment';
import {
  CREATE_FINANCIAL_TRANSACTION,
  LIST_FINANCIAL_TRANSACTIONS,
} from '@/data/actions/modules/financial';
import Forbidden from '@/components/auth/Forbidden.vue';
import AttentionBar from './AttentionBar.vue';
import TodayCard from './TodayCard.vue';
import AccountsCard from './AccountsCard.vue';
import ReceivablesCard from './ReceivablesCard.vue';
import ExpensesCard from './ExpensesCard.vue';
import HealthBand from './HealthBand.vue';
import UpcomingChart from './UpcomingChart.vue';
import EvolutionChart from './EvolutionChart.vue';
import {
  PREFERENCES_KEY, REGIMES, blankOverview, blankPeriod, longDate,
} from './helpers';

function currentMonth() {
  return {
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
  };
}

export default {
  components: {
    Forbidden,
    AttentionBar,
    TodayCard,
    AccountsCard,
    ReceivablesCard,
    ExpensesCard,
    HealthBand,
    UpcomingChart,
    EvolutionChart,
  },
  data() {
    const period = currentMonth();
    return {
      regime: REGIMES.competence,
      hidden: false,
      period,
      appliedPeriod: { ...period },
      overview: blankOverview(),
      periodData: blankPeriod(),
      months: [],
      loading: {
        overview: false,
        period: false,
        evolution: false,
      },
      periodTimer: null,
      periods: [
        { value: 'month', text: 'Este mês' },
        { value: 'd:-30', text: 'Últimos 30 dias' },
        { value: 'd:-90', text: 'Últimos 3 meses' },
        { value: 'd:-180', text: 'Últimos 6 meses' },
      ],
    };
  },
  computed: {
    hasAccess() {
      return this.$can(LIST_FINANCIAL_TRANSACTIONS);
    },
    canCreate() {
      return this.$can(CREATE_FINANCIAL_TRANSACTION);
    },
    todayLabel() {
      return longDate(this.overview.date);
    },
    periodLabel() {
      const start = moment(this.appliedPeriod.startDate, 'YYYY-MM-DD');
      const end = moment(this.appliedPeriod.endDate, 'YYYY-MM-DD');
      if (start.isSame(start.clone().startOf('month'), 'day')
        && end.isSame(start.clone().endOf('month'), 'day')) {
        return start.format('MMMM [de] YYYY');
      }
      return `${start.format('DD/MM/YYYY')} a ${end.format('DD/MM/YYYY')}`;
    },
    periodValid() {
      const { startDate, endDate } = this.period;
      const start = moment(startDate, 'YYYY-MM-DD', true);
      const end = moment(endDate, 'YYYY-MM-DD', true);
      return start.isValid() && end.isValid() && !end.isBefore(start)
        && end.diff(start, 'months', true) <= 6;
    },
  },
  watch: {
    period: {
      deep: true,
      handler() {
        clearTimeout(this.periodTimer);
        if (!this.periodValid) {
          return;
        }
        this.periodTimer = setTimeout(() => {
          this.appliedPeriod = { ...this.period };
          this.loadPeriod();
        }, 300);
      },
    },
  },
  async mounted() {
    if (!this.hasAccess) {
      return;
    }
    await this.loadPreferences();
    this.loadOverview();
    this.loadPeriod();
    this.loadEvolution();
  },
  beforeDestroy() {
    clearTimeout(this.periodTimer);
  },
  methods: {
    async loadPreferences() {
      try {
        const preferences = await localforage.getItem(PREFERENCES_KEY);
        if (preferences) {
          this.regime = preferences.regime === REGIMES.cash ? REGIMES.cash : REGIMES.competence;
          this.hidden = !!preferences.hidden;
        }
      } catch (e) {
        this.regime = REGIMES.competence;
      }
    },
    savePreferences() {
      return localforage.setItem(PREFERENCES_KEY, { regime: this.regime, hidden: this.hidden })
        .catch(() => {});
    },
    setRegime(regime) {
      if (this.regime === regime) {
        return;
      }
      this.regime = regime;
      this.savePreferences();
      this.loadPeriod();
      this.loadEvolution();
    },
    toggleHidden() {
      this.hidden = !this.hidden;
      this.savePreferences();
    },
    loadOverview() {
      this.loading.overview = true;
      return this.$http.get('/financial-dashboard/overview')
        .then(({ data }) => {
          this.overview = data;
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading.overview = false;
        });
    },
    loadPeriod() {
      this.loading.period = true;
      const params = { ...this.appliedPeriod, regime: this.regime };
      return this.$http.get('/financial-dashboard/period', { params })
        .then(({ data }) => {
          if (data.startDate === this.appliedPeriod.startDate
            && data.endDate === this.appliedPeriod.endDate && data.regime === this.regime) {
            this.periodData = data;
          }
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading.period = false;
        });
    },
    loadEvolution() {
      this.loading.evolution = true;
      const { regime } = this;
      return this.$http.get('/financial-dashboard/evolution', { params: { regime } })
        .then(({ data }) => {
          if (data.regime === this.regime) {
            this.months = data.months;
          }
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading.evolution = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./overview.scss";
</style>
